import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchAppointmentDetails } from '../../../redux/actions/appointmentsActions';
import { fetchAnthropometryData } from '../../../redux/actions/anthropometryActions';
import { createNewConsultation, createNewConsultationAnamnese, fetchConsultationTypes } from '../../../redux/actions/consultationActions';
import { createNewDiet, fetchDietByPatientId } from '../../../redux/actions/dietActions';
import { clearAnthropometry } from '../../../redux/slices/anthropometrySlice';
import { Tabs, Tab } from '../../../components/Tabs/Tabs';
import {
  FaUserMd, FaUser, FaWeight, FaRulerVertical, FaCalculator,
  FaPercentage, FaTransgenderAlt, FaCalendar, FaPlusCircle, FaMinusCircle,
  FaCoffee, FaAppleAlt, FaUtensils, FaPizzaSlice, FaDrumstickBite, FaIceCream,
} from "react-icons/fa";

import { FaUserDoctor } from 'react-icons/fa6';

import { Formik, Field, Form, FieldArray, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import {
  handleKeyDown, onlyNumbers, formatDietName, formatTime, tabFieldMapping,
  formatDate, calculateAge, calculateFees, formatGender, formatToMySQLDatetime
} from '../../../utils/functionsFormatPatterns/functionFormatConsultation';

import { clearCurrentDiet } from '../../../redux/slices/dietSlice';
import Select from 'react-select';
import '../styles/CreateConsultation.css';
import '../../../styles/ReactSelect.css';
import { useModalFeedback } from '../../../context/ModalContext';

const CreateConsultation = () => {
  const { appointment_id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { showInfo } = useModalFeedback();

  const addBtn = "Adicionar";
  const remBtn = "Remover";
  const { currentAppointment: appointmentDetails, loading: appointmentLoading } = useSelector((state) => state.appointments);
  const { anthropometryData, loading: anthropometryLoading } = useSelector((state) => state.anthropometry);
  // const { currentDiet, loading: currentDietLoading, error: dietError } = useSelector((state) => state.diets);
  const { diets = [], loading: currentDietLoading, error: dietError } = useSelector((state) => state.diets);
  const currentDiet = diets.length > 0 ? diets : [];
  const { consultationTypes, typesLoading, typesError } = useSelector(state => state.consultations);
  const [totalCalories, setTotalCalories] = useState(0);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [onLayoutChange] = useState('');
  const [selectedLayout, setSelectedLayout] = useState('horizontal'); // padrão
  const [dietId, setDietId] = useState([]);

  useEffect(() => {
    if (!location.state) {
      // console.error("Erro: location.state não está definido. Redirecionando para /home/appointment/get.");
      navigate(`/home/appointment/get/$`);
    }
  }, [location.state, navigate]);

  useEffect(() => {
    if (!appointment_id) {
      console.error("Erro: appointment_id não fornecido. Redirecionando para a lista de agendamentos.");
      navigate("/home/appointment/get");
      return;
    }
    // Fetch appointment details e configure o estado inicial
    dispatch(fetchAppointmentDetails(appointment_id));
  }, [appointment_id, dispatch, navigate]);


  useEffect(() => {
    const patientId = appointmentDetails?.patient_id || location.state?.patient_id || null;

    if (patientId) {
      setSelectedPatient({
        value: patientId,
        label: appointmentDetails?.patient_name || "Paciente não identificado",
      });
    }
  }, [appointmentDetails, location.state]);

  useEffect(() => {
    const patientIdFromState = location.state?.patient_id || null; // Verifica se existe location.state
    const patientIdFromAppointment = appointmentDetails?.patient_id || null;

    if (patientIdFromState) {
      setSelectedPatient({ value: patientIdFromState, label: appointmentDetails?.patient_name });
    } else if (patientIdFromAppointment) {
      setSelectedPatient({ value: patientIdFromAppointment, label: appointmentDetails?.patient_name });
    }
  }, [location.state, appointmentDetails]);


  useEffect(() => {
    dispatch(fetchAppointmentDetails(appointment_id));
  }, [dispatch, appointment_id]);

  useEffect(() => {
    if (appointmentDetails?.patient_id) {
      dispatch(clearAnthropometry());
      dispatch(fetchAnthropometryData(appointmentDetails.patient_id));
    }
  }, [dispatch, appointmentDetails?.patient_id]);


  // Initialize selectedPatient from location.state or appointmentDetails
  useEffect(() => {
    const patientIdFromState = location.state?.patient_id;
    const patientIdFromAppointment = appointmentDetails?.patient_id;

    if (patientIdFromState) {
      setSelectedPatient({ value: patientIdFromState, label: appointmentDetails?.patient_name });
    } else if (patientIdFromAppointment) {
      setSelectedPatient({ value: patientIdFromAppointment, label: appointmentDetails?.patient_name });
    }
  }, [location.state, appointmentDetails]);


  useEffect(() => {
    if (location.state?.appointmentDetails?.patient_id) {
      const patient = appointmentDetails?.patient_id === location.state.appointmentDetails.patient_id
        ? appointmentDetails
        : null;

      if (patient) {
        setSelectedPatient({ value: patient.patient_id, label: patient.patient_name });
      }
    }
  }, [location.state, appointmentDetails]);


  useEffect(() => {
    if (appointmentDetails?.patient_id) {
      dispatch(clearCurrentDiet());
      dispatch(fetchDietByPatientId(appointmentDetails.patient_id));
    }
  }, [dispatch, appointmentDetails?.patient_id]);

  useEffect(() => {
    if (currentDiet && Array.isArray(currentDiet)) {
      // Calcula o total de calorias das dietas
      const total = currentDiet.reduce((dietAcc, diet) => {
        const dietTotal = diet.meals?.reduce((mealAcc, meal) => {
          // Certifique-se de que total_calories é válido e convertível para número
          return mealAcc + (parseFloat(meal.total_calories) || 0);
        }, 0) || 0;
        return dietAcc + dietTotal;
      }, 0);

      setTotalCalories(total.toFixed(2)); // Limita a 2 casas decimais para exibição
    }
  }, [currentDiet]);

  useEffect(() => {
    dispatch(fetchConsultationTypes());
  }, [dispatch]);

  if (appointmentLoading || anthropometryLoading) {
    return <div>Carregando...</div>;
  }
  if (!appointmentDetails) {
    return <div>Erro ao carregar os dados do agendamento.</div>;
  }

  const getMealIcon = (mealName) => {
    switch (mealName.toLowerCase()) {
      case "café da manhã":
        return <FaCoffee className="icon" />;
      case "lanche da manhã":
        return <FaAppleAlt className="icon" />;
      case "almoço":
        return <FaUtensils className="icon" />;
      case "lanche da tarde":
        return <FaPizzaSlice className="icon" />;
      case "jantar":
        return <FaDrumstickBite className="icon" />;
      case "ceia":
        return <FaIceCream className="icon" />;
      default:
        return <FaUtensils className="icon" />; // Ícone padrão
    }
  };


  // Função para redirecionar para a página de Nova Antropometria
  const handleNewAnthropometry = () => {
    if (appointmentDetails?.patient_id) {
      navigate('/home/anthropometry/create', {
        state: {
          appointment_id,
          patient_id: appointmentDetails?.patient_id,
          professional_id: appointmentDetails?.professional_id,
          consultation_id: appointmentDetails?.consultation_id,
        }
      });
      // console.log(appointmentDetails.patient_id);
    } else {
      alert('Paciente não encontrado.');
    }
  };

  //
  const handleNewDiet = () => {
    navigate('/home/diet/create', {
      state: {
        appointment_id,
        patient_id: appointmentDetails?.patient_id,
        consultation_id: appointmentDetails?.consultation_id,
        returnPath: `/home/consultation/create/${appointment_id}`,
      }
    });
  }

  // Função para carregar todas as antropometrias do paciente
  const handleAllAnthropometry = () => {
    if (selectedPatient?.value) {
      navigate('/home/anthropometry/get', { state: { patient_id: selectedPatient.value } });
    } else {
      console.error('Nenhum paciente selecionado');
    }
  };

  //
  const handleAllDiet = () => {
    if (selectedPatient?.value) {
      navigate('/home/diet/get', { state: { patient_id: selectedPatient.value } });
    } else {
      console.error('Nenhum paciente selecionado');
    }
  };
  //
  const handleClickBack = () => {
    navigate("/home/appointment/get");
  };

  const handleLayoutChange = (layout) => {
    setSelectedLayout(layout);
    if (onLayoutChange) {
      onLayoutChange(layout);
    }
  };

  const initialValues = {
    // Dados básicos da consulta
    appointment_id: appointmentDetails?.appointment_id || appointment_id || '',
    professional_id: appointmentDetails?.professional_id || '',
    company_id: appointmentDetails?.company_id || '',
    patient_id: appointmentDetails?.patient_id || '',
    consult_date: new Date().toISOString().split('T')[0],
    start_time: new Date().toTimeString().slice(0, 5),
    end_time: new Date().toTimeString().slice(0, 5),

    // Valores e Taxas/Descontos da consulta
    consultation_type_id: '',
    base_fee: 0,
    additional_charges: 0,
    discounts: 0,
    final_fee: 0,
    current_weight: '',
    consult_weight: '',

    // Dados antropométricos
    late_measure: anthropometryData?.[0]?.measurement_date
      ? formatToMySQLDatetime(anthropometryData[0].measurement_date)
      : '',
    late_height: anthropometryData?.[0]?.height ? Number.parseFloat(anthropometryData[0].height) : '',
    late_weight: anthropometryData?.[0]?.current_weight ? Number.parseFloat(anthropometryData[0].current_weight) : '',
    late_bmi: anthropometryData?.[0]?.bmi ? Number.parseFloat(anthropometryData[0].bmi) : '',
    late_body_fat_percentage: anthropometryData?.[0]?.body_fat_percentage
      ? Number.parseFloat(anthropometryData[0].body_fat_percentage)
      : '',
    late_bray_classification: anthropometryData?.[0]?.bray_classification || '',

    // Seção 1: Objetivo da Consulta
    objectiveQ1: '',
    objectiveQ2: '',
    objectiveQ3: '',

    // Seção 2: Histórico Clínico
    current_illnesses: [''],
    past_illnesses: [''],
    allergies: [''],
    surgeries: [''],
    medications: [''],
    family_history: [''],

    // Seção 3: Histórico Alimentar
    foodHistoryQ1: '',
    foodHistoryQ2: '',
    foodHistoryQ3: '',
    foodHistoryQ4: '',
    foodHistoryQ5: '',
    foodHistoryQ6: '',
    foodHistoryQ7: '',
    foodHistoryQ8: '',
    foodHistoryQ9: '',

    // Seção 4: Hidratação
    hydrationQ1: '',
    hydrationQ2: '',
    hydrationQ3: '',

    // Seção 5: Hábitos de Vida
    habitsQ1: '',
    habitsQ2: '',
    habitsQ3: '',
    habitsQ4: '',
    habitsQ5: '',

    // Seção 6: Fatores Emocionais
    emotionalFactorsQ1: '',
    emotionalFactorsQ2: '',
    emotionalFactorsQ3: '',
    emotionalFactorsQ4: '',

    // Seção 7: Suplementação
    supplementsQ1: '',
    supplementsQ2: '',

    // Seção 8: Expectativas
    expectationsQ1: '',
    expectationsQ2: '',
    expectationsQ3: '',

    // Dados finais
    diet_name: appointmentDetails?.diet_name || '',
    procedures: '',
    diagnosis: '',
    observations: ''
  };


  const validationSchema = Yup.object({
    // Dados básicos da consulta (necessários)
    consult_date: Yup.date().required('Data da consulta é necessário'),
    start_time: Yup.string().required('Hora de início é necessário'),
    consult_weight: Yup.number()
      .transform((value, originalValue) => (originalValue === '' ? undefined : value))
      .min(1, 'Peso inválido'),
    // .required('Peso atual é necessário'),
    // consultation_type_id: Yup.number().required('Tipo de consulta é necessário'),


    // Dados antropométricos (opcionais)
    late_height: Yup.number().min(0, 'Altura inválida'),
    late_weight: Yup.number().min(0, 'Peso inválido'),
    late_bmi: Yup.number().min(0, 'IMC inválido'),
    late_body_fat_percentage: Yup.number().min(0, 'Percentual inválido'),

    // Seção 1: Objetivo da Consulta
    objectiveQ1: Yup.string().required('Campo necessário'),
    // objectiveQ2: Yup.string().required('Campo necessário'),
    // objectiveQ3: Yup.string().required('Campo necessário'),

    // Seção 2: Histórico Clínico
    current_illnesses: Yup.array().of(Yup.string()),
    past_illnesses: Yup.array().of(Yup.string()),
    allergies: Yup.array().of(Yup.string()),
    surgeries: Yup.array().of(Yup.string()),
    medications: Yup.array().of(Yup.string()),
    family_history: Yup.array().of(Yup.string()),

    // Seção 3: Histórico Alimentar
    // foodHistoryQ1: Yup.string().required('Campo necessário'),
    // foodHistoryQ2: Yup.string().required('Campo necessário'),
    // foodHistoryQ3: Yup.string().required('Campo necessário'),
    // foodHistoryQ4: Yup.string().required('Campo necessário'),
    foodHistoryQ5: Yup.string().required('Campo necessário'),
    // foodHistoryQ6: Yup.string().required('Campo necessário'),
    // foodHistoryQ7: Yup.string().required('Campo necessário'),
    // foodHistoryQ8: Yup.string().required('Campo necessário'),
    // foodHistoryQ9: Yup.string().required('Campo necessário'),

    // Seção 4: Hidratação
    // hydrationQ1: Yup.string().required('Campo necessário'),
    // hydrationQ2: Yup.string().required('Campo necessário'),
    hydrationQ3: Yup.string().required('Campo necessário'),

    // Seção 5: Hábitos de Vida
    // habitsQ1: Yup.string().required('Campo necessário'),
    // habitsQ2: Yup.string().required('Campo necessário'),
    // habitsQ3: Yup.string().required('Campo necessário'),
    // habitsQ4: Yup.string().required('Campo necessário'),
    habitsQ5: Yup.string().required('Campo necessário'),

    // Seção 6: Fatores Emocionais
    emotionalFactorsQ1: Yup.string().required('Campo necessário'),
    emotionalFactorsQ2: Yup.string().required('Campo necessário'),
    emotionalFactorsQ3: Yup.string().required('Campo necessário'),
    emotionalFactorsQ4: Yup.string().required('Campo necessário'),

    // Seção 7: Suplementação
    supplementsQ1: Yup.string().required('Campo necessário'),
    // supplementsQ2: Yup.string().required('Campo necessário'),

    // Seção 8: Expectativas
    expectationsQ1: Yup.string().required('Campo necessário'),
    // expectationsQ2: Yup.string().required('Campo necessário'),
    // expectationsQ3: Yup.string().required('Campo necessário'),

    // Seção 9: Dados finais (necessários)
    procedures: Yup.string().required('Procedimentos são necessários'),
    diagnosis: Yup.string().required('Diagnóstico é necessário'),
    observations: Yup.string()
  });


  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const formattedValues = {
        ...values,
        late_height: Number.parseFloat(values.late_height) || 0,
        late_weight: Number.parseFloat(values.late_weight) || 0,
        late_bmi: Number.parseFloat(values.late_bmi) || 0,
        late_body_fat_percentage: Number.parseFloat(values.late_body_fat_percentage) || 0,
        current_illnesses: values.current_illnesses.filter((item) => item.trim() !== ''),
        past_illnesses: values.past_illnesses.filter((item) => item.trim() !== ''),
        allergies: values.allergies.filter((item) => item.trim() !== ''),
        surgeries: values.surgeries.filter((item) => item.trim() !== ''),
        medications: values.medications.filter((item) => item.trim() !== ''),
        family_history: values.family_history.filter((item) => item.trim() !== ''),
      };

      // console.log('Dados enviados:', formattedValues);


      // Captura o payload e extrai a propriedade consultation_id
      const response = await dispatch(createNewConsultation(formattedValues)).unwrap();
      const consultation_id = response.consultation_id; // Extraindo corretamente o ID

      // 3. Prepara os dados da anamnese
      const anamneseData = {
        version: "1.0", // Versão do formulário
        objective: {
          q1: values.objectiveQ1 || null, // Motivo principal
          q2: values.objectiveQ2 || null, // Metas específicas
          q3: values.objectiveQ3 || null  // Dificuldades alimentares
        },
        clinicalHistory: {
          currentIllnesses: values.current_illnesses.filter(item => item.trim() !== ''),
          pastIllnesses: values.past_illnesses.filter(item => item.trim() !== ''),
          allergies: values.allergies.filter(item => item.trim() !== ''),
          surgeries: values.surgeries.filter(item => item.trim() !== ''),
          medications: values.medications.filter(item => item.trim() !== ''),
          familyHistory: values.family_history.filter(item => item.trim() !== '')
        },
        foodHistory: {
          q1: values.foodHistoryQ1 || null, // Refeições diárias
          q2: values.foodHistoryQ2 || null, // Pular refeições
          q3: values.foodHistoryQ3 || null, // Descrição refeições
          q4: values.foodHistoryQ4 || null, // Lanches
          q5: values.foodHistoryQ5 || null, // Preferência doce/salgado
          q6: values.foodHistoryQ6 || null, // Fast food
          q7: values.foodHistoryQ7 || null, // Restrições
          q8: values.foodHistoryQ8 || null, // Habilidades culinárias
          q9: values.foodHistoryQ9 || null  // Comer fora/delivery
        },
        hydration: {
          q1: values.hydrationQ1 || null, // Água diária
          q2: values.hydrationQ2 || null, // Outras bebidas
          q3: values.hydrationQ3 || null  // Sensação de sede
        },
        habits: {
          q1: values.habitsQ1 || null, // Atividade física
          q2: values.habitsQ2 || null, // Nível de estresse
          q3: values.habitsQ3 || null, // Qualidade do sono
          q4: values.habitsQ4 || null, // Regularidade horários
          q5: values.habitsQ5 || null  // Álcool/tabaco
        },
        emotionalFactors: {
          q1: values.emotionalFactorsQ1 || null, // Ansiedade
          q2: values.emotionalFactorsQ2 || null, // Comer emocional
          q3: values.emotionalFactorsQ3 || null, // Exageros
          q4: values.emotionalFactorsQ4 || null  // Hábitos para mudar
        },
        supplementation: {
          q1: values.supplementsQ1 || null, // Uso de suplementos
          q2: values.supplementsQ2 || null  // Orientação
        },
        expectations: {
          q1: values.expectationsQ1 || null, // Objetivos
          q2: values.expectationsQ2 || null, // Disposição
          q3: values.expectationsQ3 || null  // Preferências
        }
      };

      // 4. Salva a anamnese separadamente
      try {
        await dispatch(createNewConsultationAnamnese({
          consultation_id,
          data: anamneseData
        })).unwrap();

        console.log('Anamnese salva com sucesso');
      } catch (error) {
        console.error('Erro ao salvar anamnese:', error);
        // Não interrompe o fluxo principal, apenas registra o erro
      }

      // Limpa o estado antes de redirecionar
      dispatch(clearAnthropometry());
      dispatch(clearCurrentDiet());

      // setMessage("Consulta criada com sucesso!");
      // setShowModal(true);
      // setTimeout(() => {
      //   setShowModal(false);
      //   resetForm();
      //   navigate(`/home/consultation/show/${consultation_id}`);
      // }, 2000);
      showInfo("Consulta criada com sucesso!");
      resetForm();
      navigate(`/home/consultation/show/${consultation_id}`);
    } catch (error) {
      console.error("Erro ao criar consulta:", error);
      const errorMsg = (error.response?.data?.message)
        ? error.response.data.message
        : 'Erro ao enviar formulário. Tente novamente mais tarde.';
      // setMessage(errorMsg);
      // setShowModal(true);
      // setTimeout(() => {
      //   setShowModal(false);
      // }, 2500);
      showInfo(errorMsg, 3000)
    } finally {
      setSubmitting(false);
    }
  };

  if (!appointmentDetails) {
    return <div>Carregando os detalhes do agendamento...</div>;
  }

  return (
    <div className="consultationMain">
      <h3>Nova Consulta</h3>
      {/* <div>Agendamento id: {appointmentDetails?.appointment_id}</div>
      <div>Paciente: {appointmentDetails?.patient_id}</div>
      <div>Professsional: {appointmentDetails?.professional_id}</div>
      <div>Antropometria id: {anthropometryData?.anthropometry_id}</div>
      <div>Dieta id: {dietId}</div> */}
      <div className="consultationForm">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >

          {({ isSubmitting, errors, touched, setFieldValue, values }) => (
            <Form onKeyDown={handleKeyDown}>
              {/* <div className="consultationLine">
                <div className="consultation-form-group">
                  <label>Data da Consulta:</label>
                  <Field type="date" id="consultation_date" name="consult_date" />
                  {errors.consult_date && touched.consult_date && <div className="error">{errors.consult_date}</div>}
                </div>

                <div className="consultation-form-group">
                  <label>Hora de Início:</label>
                  <Field type="time" id="consultation_time" name="start_time" />
                  {errors.start_time && touched.start_time && <div className="error">{errors.start_time}</div>}
                </div>

                <div className="consultation-form-group">
                  <label htmlFor="consultation_type_id">Tipo de Consulta:</label>
                  {typesLoading ? (
                    <div>Carregando tipos de consulta...</div>
                  ) : typesError ? (
                    <div className="error">Erro ao carregar tipos: {typesError}</div>
                  ) : (
                    <Select
                      name="consultation_type_id"
                      options={consultationTypes.map(type => ({
                        value: type.consultation_type_id,
                        label: `${type.name}`,
                        // label: `${type.name} (R$ ${type.default_fee.toFixed(2)})`,
                        fee: type.default_fee // Podemos armazenar a taxa para uso posterior
                      }))}
                      onChange={(selectedOption) => {
                        setFieldValue('consultation_type_id', selectedOption.value);
                        setFieldValue('base_fee', selectedOption.fee);
                        setFieldValue('final_fee', calculateFees(selectedOption.fee, values.additional_charges, values.discounts));
                      }}

                      placeholder="Selecione o tipo de consulta"
                      noOptionsMessage={() => "Nenhum tipo disponível"}
                      classNamePrefix="custom-select"
                    />
                  )}
                  <ErrorMessage name="consultation_type_id" component="div" className="error" />
                </div>
              </div> */}

              {/* 
              <div className="consultationLine">
                Campo Taxa Base
                <div className="consultation-form-group">
                  <label>Taxa Base (R$)</label>
                  <Field
                    name="base_fee"
                    onBlur={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      setFieldValue('base_fee', value.toFixed(2));
                      setFieldValue('final_fee', calculateFees(
                        value,
                        values.additional_charges,
                        values.discounts
                      ));
                    }}
                    onChange={(e) => {
                      // Permite digitação livre, só formata no blur
                      setFieldValue('base_fee', e.target.value);
                    }}
                    value={values.base_fee}
                    type="number"
                    step="5.00"
                    className="form-control"
                  />
                </div>

                Campo Encargos Adicionais
                <div className="consultation-form-group">
                  <label>Encargos Adicionais (R$)</label>
                  <Field
                    name="additional_charges"
                    onBlur={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      setFieldValue('additional_charges', value.toFixed(2));
                      setFieldValue('final_fee', calculateFees(
                        values.base_fee,
                        value,
                        values.discounts
                      ));
                    }}
                    onChange={(e) => {
                      setFieldValue('additional_charges', e.target.value);
                    }}
                    value={values.additional_charges}
                    type="number"
                    step="5.00"
                    className="form-control"
                  />
                </div>

                Campo Descontos
                <div className="consultation-form-group">
                  <label>Descontos (R$)</label>
                  <Field
                    name="discounts"
                    onBlur={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      setFieldValue('discounts', value.toFixed(2));
                      setFieldValue('final_fee', calculateFees(
                        values.base_fee,
                        values.additional_charges,
                        value
                      ));
                    }}
                    onChange={(e) => {
                      setFieldValue('discounts', e.target.value);
                    }}
                    value={values.discounts}
                    type="number"
                    step="5.00"
                    className="form-control"
                  />
                </div>

                Campo Valor Final (somente leitura)
                <div className="consultation-form-group">
                  <label>Valor Final (R$)</label>
                  <input
                    type="text"
                    value={values.final_fee ? parseFloat(values.final_fee).toFixed(2) : '0.00'}
                    readOnly
                    disabled={values.final_fee}
                    className="form-control"
                  />
                </div>
              </div>
               */}

              <div className="consultationLine">
                <div className="consultation-form-group">
                  <label htmlFor="consultation_types">Empresa:</label>
                  <div className='box-line'>
                    <div className="boxCol1">
                      <span className="spanDataTitle"><FaUserMd className="icon" /> {appointmentDetails.company_name}</span>
                    </div>
                  </div>
                </div>

                <div className="consultation-form-group">
                  <label>Profissional:</label>
                  <div className='box-line'>
                    <div className="boxCol1">
                      <span className="spanDataTitle"><FaUserMd className="icon" /> {appointmentDetails.professional_name}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="consultation-form-group">
                <label>Paciente:</label>
                <div className='box-line'>
                  <div className="boxCol1">
                    <span className="spanDataTitle">{<FaUser className='icon' />} {appointmentDetails.patient_name}</span>
                    <span className="spanDataTitle">{<FaCalendar className='icon' />} {appointmentDetails.patient_age ? `${calculateAge(appointmentDetails.patient_age)}` : ''}</span>
                    <span className="spanDataTitle"> {<FaTransgenderAlt className='icon' />} {appointmentDetails.patient_gender ? `${formatGender(appointmentDetails.patient_gender)}` : ''}</span>
                  </div>
                </div>
              </div>

              {/* Antropometria */}
              {/* <div className="consultation-form-group">
                <label>Antropometria anterior do Paciente (mais recente disponível) :</label>
                <div className="consultationLine"></div>
                {anthropometryData && anthropometryData.length > 0 ? (
                  <div className="consultation-form-group">
                    <ul className="anthropometry-list">
                      <li className="anthropometry-item">
                        <FaCalendar className="icon" />
                        <span>Data: {formatDate(anthropometryData[0]?.measurement_date)}</span>
                      </li>
                      <li className="anthropometry-item">
                        <FaRulerVertical className="icon" />
                        <span>Altura: {anthropometryData[0]?.height} cm</span>
                      </li>
                      <li className="anthropometry-item">
                        <FaWeight className="icon" />
                        <span>Peso Atual: {anthropometryData[0]?.current_weight} kg</span>
                      </li>
                      <li className="anthropometry-item">
                        <FaCalculator className="icon" />
                        <span>IMC: {anthropometryData[0]?.bmi} kg/m²</span>
                      </li>
                      <li className="anthropometry-item">
                        <FaUserMd className="icon" />
                        <span>Classificação: {anthropometryData[0]?.bray_classification}</span>
                      </li>
                      <li className="anthropometry-item">
                        <FaPercentage className="icon" />
                        <span>P.G: {anthropometryData[0]?.body_fat_percentage} %</span>
                      </li>
                    </ul>
                  </div>
                ) : (
                  <>
                    <div className="consultationLine">
                      <span className='spanDataTitle'>Dados antropométricos não disponíveis</span>

                      <div className="anthropometryButtons">
                        <button className="sendBtn" type="button" onClick={handleNewAnthropometry}>Nova Antropometria</button>
                       // <button className="sendBtn" type="button" onClick={handleAllAnthropometry}>Todas Antropometrias</button>
                       // <button className="sendBtn" onClick={() => navigate('/anthropometry-evolution')}>Evolução Antropométrica</button>
                      </div>
                    </div>
                  </>
                )}
              </div> */}

              {/* dietas */}
              {/* 
              <div className="consultation-form-group">
                <label>Dietas anteriores do Paciente (mais recente disponível) :</label>
              </div>
              {
                Array.isArray(currentDiet) && currentDiet.length > 0 ? (
                  <div className="consultation-form-group">
                    {currentDiet.map((diet) => (
                      <div className="box-diet" key={diet.diet_id}>
                        {setDietId(diet.diet_id)}
                        // Coluna A - Informações da dieta
              <div className="box-diet-col-A">
                {diet ? (
                  <>
                    <div>Nome: <span className="spanDataTitle">{formatDietName(diet.name)}</span></div>
                    <div>Criada em: <span className="spanDataTitle">{formatDate(diet.created_at)}</span></div>
                    <div>Objetivo: <span className="spanDataTitle">{diet.objective}</span></div>
                    <div>Total de Calorias: <span className="spanDataTitle">{totalCalories}</span> kcal</div>
                  </>
                ) : (
                  <div>Dados da dieta não disponíveis</div>
                )}
              </div>
              <div className="box-diet-col-A" key={diet.diet_id}>
                          <div>Nome: <span className="spanDataTitle">{formatDietName(diet.name)}</span></div>
                          <div>Criada em: <span className="spanDataTitle">{formatDate(diet.created_at)}</span></div>
                          <div>Objetivo: <span className="spanDataTitle">{diet.objective}</span></div>
                          <div>Total de Calorias: <span className="spanDataTitle">{totalCalories}</span> kcal</div>
                        </div> 

              // Coluna B - Lista de Refeições - aqui pode vir o detalhamento das refeições da dieta
               {diet.meals && diet.meals.length > 0 && (
                          <div className="box-diet-col-B">
                            {diet.meals.map((meal) => (
                              <div key={meal.meal_id} className='meal-card'>
                                <div className="meal-line">
                                  <div className="label-card"><div className="label-icon">{getMealIcon(meal.name)}</div></div>
                                  <div className="meal-title"><span className="spanDataTitle">{meal.name} {" "}</span> </div>
                                  <div className="meal-time">Hora: <span className="spanDataTitle">{formatTime(meal.time)} {" "}</span></div>
                                  <div className="meal-kcal">Calorias: <span className="spanDataTitle">{meal.total_calories}</span> kcal</div>
                                </div>

                       Lista de Alimentos
                                {meal.foods && meal.foods.length > 0 && (
                                  <div>
                                    {meal.foods.map((food, index) => (
                                      <div key={index.food_name} className="food-line">
                                        <ul className='dietFoodLine'>
                                          <li>
                                            <div className="liCol">
                                              <label htmlFor="">Quant</label>
                                              <div><span className="spanDataTitle">{food.quantity}{" "} </span>{food.unit_measurement} {" "}</div>
                                            </div>
                                            <div className="liCol">
                                              <label htmlFor="">Alimento: </label>
                                              <div><span className="spanDataTitle">{food.food_name} {" "}</span></div>
                                            </div>
                                            <div className="liCol liCalories">
                                              <label htmlFor="">Calorias: </label>
                                              <div><span className="spanDataTitle">{food.calories}</span> kcal</div>
                                            </div>
                                          </li>
                                        </ul>
                                      </div>
                                    ))}
                                  </div>
                                )
                                }
                              </div>
                            ))}
                          </div>
                        )}


            </div>
          ))}
      </div>

      ) : (
      <>
        <div className="consultationLine">

          <span className='spanDataTitle'>Não há dietas cadastradas para este paciente.</span>

          <div className="anthropometryButtons">
            <button className="sendBtn" type="button" onClick={handleNewDiet}>Nova Dieta</button>

                      // <button className="sendBtn" type="button" onClick={handleAllDiet}>Todas as Dietas</button>
                      // <button className="sendBtn" onClick={() => navigate('/anthropometry-evolution')}>Evolução Antropométrica</button>
          </div>
        </div>
      </>
      )
              } */}

              <div className="consultation-form-group">
                <div>
                  {/* Escolha de visualização */}
                  <div className="rbLine">
                    <h3 className='askTitle'><FaUserDoctor className='icon' /> / <FaUser className='icon' /> Nessa Consulta (Questionário de Anamnese Nutricional)</h3>
                    <div className='consultationLineRBGroup'>
                      <input
                        id='horizontal'
                        type="radio"
                        name="layout"
                        value="horizontal"
                        checked={selectedLayout === 'horizontal'}
                        onChange={() => setSelectedLayout('horizontal')}
                      />
                      <label htmlFor='horizontal'>Visualização Horizontal</label>
                    </div>
                    <div className='consultationLineRBGroup'>
                      <input
                        id="vertical"
                        type="radio"
                        name="layout"
                        value="vertical"
                        checked={selectedLayout === 'vertical'}
                        onChange={() => setSelectedLayout('vertical')}
                      />
                      <label htmlFor='vertical'>Visualização Vertical</label>
                    </div>
                  </div>
                  {/* <h3 className='askTitle'>Questionário de Anamnese Nutricional</h3> */}
                  {/* Layouts */}
                  <div>
                    {selectedLayout === 'vertical' && (
                      <div id="vertical-layout">
                        {/* SEU LAYOUT VERTICAL AQUI */}
                        <div className="consultationLineBox">
                          <div className="consultationLine">
                            <div className="consultation-form-group">
                              <label>Data da Consulta:</label>
                              <Field type="date" id="consultation_date" name="consult_date" />
                              {errors.consult_date && touched.consult_date && <div className="error">{errors.consult_date}</div>}
                            </div>

                            <div className="consultation-form-group">
                              <label>Hora de Início:</label>
                              <Field type="time" id="consultation_time" name="start_time" />
                              {errors.start_time && touched.start_time && <div className="error">{errors.start_time}</div>}
                            </div>

                            <div className="consultation-form-group">
                              <label htmlFor="consultation_type_id">Tipo de Consulta:</label>
                              {typesLoading ? (
                                <div>Carregando tipos de consulta...</div>
                              ) : typesError ? (
                                <div className="error">Erro ao carregar tipos: {typesError}</div>
                              ) : (
                                <Select
                                  name="consultation_type_id"
                                  options={consultationTypes.map(type => ({
                                    value: type.consultation_type_id,
                                    label: `${type.name}`,
                                    // label: `${type.name} (R$ ${type.default_fee.toFixed(2)})`,
                                    fee: type.default_fee // Podemos armazenar a taxa para uso posterior
                                  }))}
                                  onChange={(selectedOption) => {
                                    setFieldValue('consultation_type_id', selectedOption.value);
                                    setFieldValue('base_fee', selectedOption.fee);
                                    setFieldValue('final_fee', calculateFees(selectedOption.fee, values.additional_charges, values.discounts));
                                  }}

                                  placeholder="Selecione o tipo de consulta"
                                  noOptionsMessage={() => "Nenhum tipo disponível"}
                                  classNamePrefix="custom-select"
                                />
                              )}
                              <ErrorMessage name="consultation_type_id" component="div" className="error" />
                            </div>
                          </div>

                          <div className="consultationLine">
                            {/* Campo Taxa Base */}
                            <div className="consultation-form-group">
                              <label>Taxa Base (R$)</label>
                              <Field
                                name="base_fee"
                                onBlur={(e) => {
                                  const value = parseFloat(e.target.value) || 0;
                                  setFieldValue('base_fee', value.toFixed(2));
                                  setFieldValue('final_fee', calculateFees(
                                    value,
                                    values.additional_charges,
                                    values.discounts
                                  ));
                                }}
                                onChange={(e) => {
                                  // Permite digitação livre, só formata no blur
                                  setFieldValue('base_fee', e.target.value);
                                }}
                                value={values.base_fee}
                                type="number"
                                step="5.00"
                                className="form-control"
                              />
                            </div>

                            {/* Campo Encargos Adicionais */}
                            <div className="consultation-form-group">
                              <label>Encargos Adicionais (R$)</label>
                              <Field
                                name="additional_charges"
                                onBlur={(e) => {
                                  const value = parseFloat(e.target.value) || 0;
                                  setFieldValue('additional_charges', value.toFixed(2));
                                  setFieldValue('final_fee', calculateFees(
                                    values.base_fee,
                                    value,
                                    values.discounts
                                  ));
                                }}
                                onChange={(e) => {
                                  setFieldValue('additional_charges', e.target.value);
                                }}
                                value={values.additional_charges}
                                type="number"
                                step="5.00"
                                className="form-control"
                              />
                            </div>

                            {/* Campo Descontos */}
                            <div className="consultation-form-group">
                              <label>Descontos (R$)</label>
                              <Field
                                name="discounts"
                                onBlur={(e) => {
                                  const value = parseFloat(e.target.value) || 0;
                                  setFieldValue('discounts', value.toFixed(2));
                                  setFieldValue('final_fee', calculateFees(
                                    values.base_fee,
                                    values.additional_charges,
                                    value
                                  ));
                                }}
                                onChange={(e) => {
                                  setFieldValue('discounts', e.target.value);
                                }}
                                value={values.discounts}
                                type="number"
                                step="5.00"
                                className="form-control"
                              />
                            </div>

                            {/* Campo Valor Final (somente leitura) */}
                            <div className="consultation-form-group">
                              <label>Valor Final (R$)</label>
                              <input
                                type="text"
                                value={values.final_fee ? parseFloat(values.final_fee).toFixed(2) : '0.00'}
                                readOnly
                                disabled={values.final_fee}
                                className="form-control"
                              />
                            </div>
                          </div>

                        </div>

                        <div className="consultation-form-group">
                          <div className="consultationLineQuestion elementHidden">
                            <label htmlFor="consult_weight">Peso Atual (kg):</label>
                            <Field
                              type="number"
                              name="consult_weight"
                              id="consult_weight"
                              className="fieldInput"
                              onKeyDown={(e) => onlyNumbers(e, setFieldValue, 'consult_weight')}
                            />
                            {errors.consult_weight && touched.consult_weight && <div className="error">{errors.consult_weight}</div>}
                          </div>
                        </div>

                        {/* Objetivo da Consulta */}
                        <div className="consultation-form-group">
                          <h3 className='askTitle'><span className='askSpan'>1</span> Objetivo da Consulta</h3>
                          <div className="consultation-form-group">
                            <div className="consultationLineQuestion">
                              <label htmlFor="consult_weight">Peso Atual (kg):</label>
                              <Field
                                type="number"
                                name="consult_weight"
                                id="consult_weight"
                                className="fieldInput"
                                onKeyDown={(e) => onlyNumbers(e, setFieldValue, 'consult_weight')}
                              />
                              {errors.consult_weight && touched.consult_weight && <div className="error">{errors.consult_weight}</div>}
                            </div>
                          </div>
                          <div className="consultationLineQuestion">
                            <label htmlFor="objectiveQ1">Qual o motivo principal que trouxe você aqui?</label>
                            <Field
                              type="text"
                              name="objectiveQ1"
                              id="objectiveQ1"
                              className="fieldInput"
                            />
                            {errors.objectiveQ1 && touched.objectiveQ1 && <div className="error">{errors.objectiveQ1}</div>}
                          </div>
                          <div className="consultationLineQuestion">
                            <label htmlFor="objectiveQ2">Tem alguma meta específica? (Ex.: emagrecimento, ganho de massa, melhora da saúde, controle de doenças, etc.)</label>
                            <Field
                              type="text"
                              name="objectiveQ2"
                              id="objectiveQ2"
                              className="fieldInput"
                            />
                            {errors.objectiveQ2 && touched.objectiveQ2 && <div className="error">{errors.objectiveQ2}</div>}
                          </div>
                          <div className="consultationLineQuestion">
                            <label htmlFor="objectiveQ3">Tem alguma dificuldade específica com sua alimentação?</label>
                            <Field
                              type="text"
                              name="objectiveQ3"
                              id="objectiveQ3"
                              className="fieldInput"
                            />
                            {errors.objectiveQ3 && touched.objectiveQ3 && <div className="error">{errors.objectiveQ3}</div>}
                          </div>
                        </div>
                        {/* Histórico Cliníco e Familiar */}
                        <div className="consultation-form-group">
                          <h3 className='askTitle'><span className='askSpan'>2</span> Histórico Clínico e Familiar</h3>

                          {/* Doenças Atuais */}
                          <div className="consultation-form-group">
                            <label>Doenças Atuais</label>
                            <FieldArray name="current_illnesses">
                              {({ push, remove }) => (
                                <div>
                                  {values.current_illnesses && values.current_illnesses.length > 0 ? (
                                    values.current_illnesses.map((illness, index) => (
                                      <div key={index} className="consultationLine">
                                        <Field
                                          type="text"
                                          name={`current_illnesses[${index}]`}
                                          placeholder={`Doença ${index + 1}`}
                                        />
                                        <button className='btnIcoAddRm' type="button" onClick={() => push('')} data-tip={addBtn}>
                                          <FaPlusCircle className='iconPlus' />
                                        </button>
                                        <button className='btnIcoAddRm' type="button" onClick={() => remove(index)} data-tip={remBtn}>
                                          <FaMinusCircle className='iconMinus' />
                                        </button>
                                      </div>
                                    ))
                                  ) : (
                                    <>
                                      <p>Nenhuma doença atual adicionada.</p>
                                      <button className='btnIcoAddRm' type="button" onClick={() => push('')} data-tip={addBtn}>
                                        <FaPlusCircle className='iconPlus' />
                                      </button>
                                    </>
                                  )}
                                </div>
                              )}
                            </FieldArray>
                          </div>

                          {/* Doenças Prévias ou Crônicas */}
                          <div className="consultation-form-group">
                            <label>Doenças Prévias ou Crônicas</label>
                            <FieldArray name="past_illnesses" id="past_illnesses">
                              {({ push, remove }) => (
                                <div>
                                  {values.past_illnesses && values.past_illnesses.length > 0 ? (
                                    values.past_illnesses.map((illness, index) => (
                                      <div key={index} className="consultationLine">
                                        <Field
                                          type="text"
                                          name={`past_illnesses[${index}]`}
                                          placeholder={`Doença ${index + 1}`}
                                        />
                                        <button className='btnIcoAddRm' type="button" onClick={() => push('')} data-tip={addBtn}>
                                          <FaPlusCircle className='iconPlus' />
                                        </button>
                                        <button className='btnIcoAddRm' type="button" onClick={() => remove(index)} data-tip={remBtn}>
                                          <FaMinusCircle className='iconMinus' />
                                        </button>
                                      </div>
                                    ))
                                  ) : (
                                    <>
                                      <p>Nenhuma doença atual adicionada.</p>
                                      <button className='btnIcoAddRm' type="button" onClick={() => push('')} data-tip={addBtn}>
                                        <FaPlusCircle className='iconPlus' />
                                      </button>
                                    </>
                                  )}
                                </div>
                              )}
                            </FieldArray>
                            {errors.past_illnesses && touched.past_illnesses && (
                              <div className="error-message">{errors.past_illnesses}</div>
                            )}
                          </div>

                          {/* Alergias e Intolerâncias */}
                          <div className="consultation-form-group">
                            <label>Alergias e Intolerâncias</label>
                            <FieldArray name="allergies" id="allergies">
                              {({ push, remove }) => (
                                <div>
                                  {values.allergies && values.allergies.length > 0 ? (
                                    values.allergies.map((allergy, index) => (
                                      <div key={index} className="consultationLine">
                                        <Field
                                          type="text"
                                          name={`allergies[${index}]`}
                                          placeholder={`Alergia ${index + 1}`}
                                        />
                                        <button className="btnIcoAddRm" type="button" onClick={() => push('')} data-tip={addBtn}>
                                          <FaPlusCircle className="iconPlus" />
                                        </button>
                                        <button className="btnIcoAddRm" type="button" onClick={() => remove(index)} data-tip={remBtn}>
                                          <FaMinusCircle className="iconMinus" />
                                        </button>
                                      </div>
                                    ))
                                  ) : (
                                    <>
                                      <p>Nenhuma alergia adicionada.</p>
                                      <button className="btnIcoAddRm" type="button" onClick={() => push('')} data-tip={addBtn}>
                                        <FaPlusCircle className="iconPlus" />
                                      </button>
                                    </>
                                  )}
                                </div>
                              )}
                            </FieldArray>
                            {errors.allergies && touched.allergies && (
                              <div className="error-message">{errors.allergies}</div>
                            )}
                          </div>

                          {/* Cirurgias e Hospitalizações */}
                          <div className="consultation-form-group">
                            <label>Cirurgias e Hospitalizações</label>
                            <FieldArray name="surgeries" id="surgeries">
                              {({ push, remove }) => (
                                <div>
                                  {values.surgeries && values.surgeries.length > 0 ? (
                                    values.surgeries.map((surgery, index) => (
                                      <div key={index} className="consultationLine">
                                        <Field
                                          type="text"
                                          name={`surgeries[${index}]`}
                                          placeholder={`Cirurgia ${index + 1}`}
                                        />
                                        <button className="btnIcoAddRm" type="button" onClick={() => push('')} data-tip={addBtn}>
                                          <FaPlusCircle className="iconPlus" />
                                        </button>
                                        <button className="btnIcoAddRm" type="button" onClick={() => remove(index)} data-tip={remBtn}>
                                          <FaMinusCircle className="iconMinus" />
                                        </button>
                                      </div>
                                    ))
                                  ) : (
                                    <>
                                      <p>Nenhuma cirurgia ou hospitalização adicionada.</p>
                                      <button className="btnIcoAddRm" type="button" onClick={() => push('')} data-tip={addBtn}>
                                        <FaPlusCircle className="iconPlus" />
                                      </button>
                                    </>
                                  )}
                                </div>
                              )}
                            </FieldArray>
                            {errors.surgeries && touched.surgeries && (
                              <div className="error-message">{errors.surgeries}</div>
                            )}
                          </div>

                          {/* Uso de Medicamentos */}
                          <div className="consultation-form-group">
                            <label>Uso de Medicamentos</label>
                            <FieldArray name="medications" id="medications">
                              {({ push, remove }) => (
                                <div>
                                  {values.medications && values.medications.length > 0 ? (
                                    values.medications.map((medication, index) => (
                                      <div key={index} className="consultationLine">
                                        <Field
                                          type="text"
                                          name={`medications[${index}]`}
                                          placeholder={`Medicamento ${index + 1}`}
                                        />
                                        <button className="btnIcoAddRm" type="button" onClick={() => push('')} data-tip={addBtn}>
                                          <FaPlusCircle className="iconPlus" />
                                        </button>
                                        <button className="btnIcoAddRm" type="button" onClick={() => remove(index)} data-tip={remBtn}>
                                          <FaMinusCircle className="iconMinus" />
                                        </button>
                                      </div>
                                    ))
                                  ) : (
                                    <>
                                      <p>Nenhum medicamento adicionado.</p>
                                      <button className="btnIcoAddRm" type="button" onClick={() => push('')} data-tip={addBtn}>
                                        <FaPlusCircle className="iconPlus" />
                                      </button>
                                    </>
                                  )}
                                </div>
                              )}
                            </FieldArray>
                            {errors.medications && touched.medications && (
                              <div className="error-message">{errors.medications}</div>
                            )}
                          </div>

                          {/* Histórico Familiar */}
                          <div className="consultation-form-group">
                            <label>Histórico Familiar</label>
                            <FieldArray name="family_history" id="family_history">
                              {({ push, remove }) => (
                                <div>
                                  {values.family_history && values.family_history.length > 0 ? (
                                    values.family_history.map((history, index) => (
                                      <div key={index} className="consultationLine">
                                        <Field
                                          type="text"
                                          name={`family_history[${index}]`}
                                          placeholder={`Histórico ${index + 1}`}
                                        />
                                        <button className="btnIcoAddRm" type="button" onClick={() => push('')} data-tip={addBtn}>
                                          <FaPlusCircle className="iconPlus" />
                                        </button>
                                        <button className="btnIcoAddRm" type="button" onClick={() => remove(index)} data-tip={remBtn}>
                                          <FaMinusCircle className="iconMinus" />
                                        </button>
                                      </div>
                                    ))
                                  ) : (
                                    <>
                                      <p>Nenhum histórico familiar adicionado.</p>
                                      <button className="btnIcoAddRm" type="button" onClick={() => push('')} data-tip={addBtn}>
                                        <FaPlusCircle className="iconPlus" />
                                      </button>
                                    </>
                                  )}
                                </div>
                              )}
                            </FieldArray>
                            {errors.family_history && touched.family_history && (
                              <div className="error-message">{errors.family_history}</div>
                            )}
                          </div>

                        </div>
                        {/* Histórico Alimentar */}
                        <div className="consultation-form-group">
                          <h3 className='askTitle'><span className='askSpan'>3</span> Histórico Alimentar</h3>

                          <div className="consultationLineQuestion">
                            <label htmlFor="foodHistoryQ1">Quantas refeições faz ao dia?</label>
                            <Field
                              type="text"
                              name="foodHistoryQ1"
                              id="foodHistoryQ1"
                              className="fieldInput"
                            />
                            {errors.foodHistoryQ1 && touched.foodHistoryQ1 && <div className="error">{errors.foodHistoryQ1}</div>}
                          </div>

                          <div className="consultationLineQuestion">
                            <label htmlFor="foodHistoryQ2">Costuma pular alguma refeição?</label>
                            <Field
                              type="text"
                              name="foodHistoryQ2"
                              id="foodHistoryQ2"
                              className="fieldInput"
                            />
                            {errors.foodHistoryQ2 && touched.foodHistoryQ2 && <div className="error">{errors.foodHistoryQ2}</div>}
                          </div>

                          <div className="consultationLineQuestion">
                            <label htmlFor="foodHistoryQ3">Como é o seu café da manhã, almoço e jantar? (Ex.: horário, composição dos pratos, quantidade)</label>
                            <Field
                              type="text"
                              name="foodHistoryQ3"
                              id="foodHistoryQ3"
                              className="fieldInput"
                            />
                            {errors.foodHistoryQ3 && touched.foodHistoryQ3 && <div className="error">{errors.foodHistoryQ3}</div>}
                          </div>

                          <div className="consultationLineQuestion">
                            <label htmlFor="foodHistoryQ4">Consome lanches entre as refeições? O que costuma comer?</label>
                            <Field
                              type="text"
                              name="foodHistoryQ4"
                              id="foodHistoryQ4"
                              className="fieldInput"
                            />
                            {errors.foodHistoryQ4 && touched.foodHistoryQ4 && <div className="error">{errors.foodHistoryQ4}</div>}
                          </div>

                          <div className="consultationLineQuestion">
                            <label htmlFor="foodHistoryQ5">Prefere alimentos doces ou salgados?</label>
                            <Field
                              type="text"
                              name="foodHistoryQ5"
                              id="foodHistoryQ5"
                              className="fieldInput"
                            />
                            {errors.foodHistoryQ5 && touched.foodHistoryQ5 && <div className="error">{errors.foodHistoryQ5}</div>}
                          </div>

                          <div className="consultationLineQuestion">
                            <label htmlFor="foodHistoryQ6">Qual a frequência de consumo de fast food e ultraprocessados?</label>
                            <Field
                              type="text"
                              name="foodHistoryQ6"
                              id="foodHistoryQ6"
                              className="fieldInput"
                            />
                            {errors.foodHistoryQ6 && touched.foodHistoryQ6 && <div className="error">{errors.foodHistoryQ6}</div>}
                          </div>

                          <div className="consultationLineQuestion">
                            <label htmlFor="foodHistoryQ7">Tem alguma restrição alimentar (ética, religiosa, voluntária)?</label>
                            <Field
                              type="text"
                              name="foodHistoryQ7"
                              id="foodHistoryQ7"
                              className="fieldInput"
                            />
                            {errors.foodHistoryQ7 && touched.foodHistoryQ7 && <div className="error">{errors.foodHistoryQ7}</div>}
                          </div>

                          <div className="consultationLineQuestion">
                            <label htmlFor="foodHistoryQ8">Sabe cozinhar? Com que frequência prepara suas refeições?</label>
                            <Field
                              type="text"
                              name="foodHistoryQ8"
                              id="foodHistoryQ8"
                              className="fieldInput"
                            />
                            {errors.foodHistoryQ8 && touched.foodHistoryQ8 && <div className="error">{errors.foodHistoryQ8}</div>}
                          </div>

                          <div className="consultationLineQuestion">
                            <label htmlFor="foodHistoryQ9">Costuma comer fora ou pedir delivery?</label>
                            <Field
                              type="text"
                              name="foodHistoryQ9"
                              id="foodHistoryQ9"
                              className="fieldInput"
                            />
                            {errors.foodHistoryQ9 && touched.foodHistoryQ9 && <div className="error">{errors.foodHistoryQ9}</div>}
                          </div>
                        </div>
                        {/*Hidratação  */}
                        <div className="consultation-form-group">
                          <h3 className='askTitle'><span className='askSpan'>4</span> Hidratação</h3>

                          <div className="consultationLineQuestion">
                            <label htmlFor="hydrationQ1">Quantos litros de água consome por dia?</label>
                            <Field
                              type="text"
                              name="hydrationQ1"
                              id="hydrationQ1"
                              className="fieldInput"
                            />
                            {errors.hydrationQ1 && touched.hydrationQ1 && <div className="error">{errors.hydrationQ1}</div>}
                          </div>

                          <div className="consultationLineQuestion">
                            <label htmlFor="hydrationQ2">Bebe outras bebidas com frequência? (Refrigerantes, sucos industrializados, álcool, etc.)</label>
                            <Field
                              type="text"
                              name="hydrationQ2"
                              id="hydrationQ2"
                              className="fieldInput"
                            />
                            {errors.hydrationQ2 && touched.hydrationQ2 && <div className="error">{errors.hydrationQ2}</div>}
                          </div>

                          <div className="consultationLineQuestion">
                            <label htmlFor="hydrationQ3">Sente sede constantemente?</label>
                            <Field
                              type="text"
                              name="hydrationQ3"
                              id="hydrationQ3"
                              className="fieldInput"
                            />
                            {errors.hydrationQ3 && touched.hydrationQ3 && <div className="error">{errors.hydrationQ3}</div>}
                          </div>
                        </div>
                        {/* `Hábitos de Vida e Rotina */}
                        <div className="consultation-form-group">
                          <h3 className='askTitle'><span className='askSpan'>5</span> Hábitos de Vida e Rotina</h3>

                          <div className="consultationLineQuestion">
                            <label htmlFor="habitsQ1">Pratica atividade física? (Qual, frequência e intensidade?)</label>
                            <Field
                              type="text"
                              name="habitsQ1"
                              id="habitsQ1"
                              className="fieldInput"
                            />
                            {errors.habitsQ1 && touched.habitsQ1 && <div className="error">{errors.habitsQ1}</div>}
                          </div>

                          <div className="consultationLineQuestion">
                            <label htmlFor="habitsQ2">Como é seu nível de estresse diário?</label>
                            <Field
                              type="text"
                              name="habitsQ2"
                              id="habitsQ2"
                              className="fieldInput"
                            />
                            {errors.habitsQ2 && touched.habitsQ2 && <div className="error">{errors.habitsQ2}</div>}
                          </div>

                          <div className="consultationLineQuestion">
                            <label htmlFor="habitsQ3">Como avalia seu sono? (Horas dormidas, qualidade do sono)</label>
                            <Field
                              type="text"
                              name="habitsQ3"
                              id="habitsQ3"
                              className="fieldInput"
                            />
                            {errors.habitsQ3 && touched.habitsQ3 && <div className="error">{errors.habitsQ3}</div>}
                          </div>

                          <div className="consultationLineQuestion">
                            <label htmlFor="habitsQ4">Tem horários regulares para refeições e descanso?</label>
                            <Field
                              type="text"
                              name="habitsQ4"
                              id="habitsQ4"
                              className="fieldInput"
                            />
                            {errors.habitsQ4 && touched.habitsQ4 && <div className="error">{errors.habitsQ4}</div>}
                          </div>

                          <div className="consultationLineQuestion">
                            <label htmlFor="habitsQ5">Faz uso de álcool ou tabaco?</label>
                            <Field
                              type="text"
                              name="habitsQ5"
                              id="habitsQ5"
                              className="fieldInput"
                            />
                            {errors.habitsQ5 && touched.habitsQ5 && <div className="error">{errors.habitsQ5}</div>}
                          </div>
                        </div>
                        {/* Fatores Emocionais e Comportamentais */}
                        <div className="consultation-form-group">
                          <h3 className='askTitle'><span className='askSpan'>6</span> Fatores Emocionais e Comportamentais</h3>

                          <div className="consultationLineQuestion">
                            <label htmlFor="emotionalFactorsQ1">Sente ansiedade ou compulsão alimentar?</label>
                            <Field as="select" name="emotionalFactorsQ1" className="fieldInput">
                              <option value="">Selecione...</option>
                              <option value="nunca">Nunca</option>
                              <option value="raramente">Raramente</option>
                              <option value="as_vezes">Às vezes</option>
                              <option value="frequentemente">Frequentemente</option>
                              <option value="sempre">Sempre</option>
                            </Field>
                            {errors.emotionalFactorsQ1 && touched.emotionalFactorsQ1 && <div className="error">{errors.emotionalFactorsQ1}</div>}
                          </div>

                          <div className="consultationLineQuestion">
                            <label htmlFor="emotionalFactorsQ2">Costuma comer por impulso ou emocionalmente?</label>
                            <Field as="select" name="emotionalFactorsQ2" className="fieldInput">
                              <option value="">Selecione...</option>
                              <option value="nunca">Nunca</option>
                              <option value="raramente">Raramente</option>
                              <option value="as_vezes">Às vezes</option>
                              <option value="frequentemente">Frequentemente</option>
                              <option value="sempre">Sempre</option>
                            </Field>
                            {errors.emotionalFactorsQ2 && touched.emotionalFactorsQ2 && <div className="error">{errors.emotionalFactorsQ2}</div>}
                          </div>

                          <div className="consultationLineQuestion">
                            <label htmlFor="emotionalFactorsQ3">Tem momentos de exagero alimentar? (Ex.: comer muito à noite, beliscar constantemente)</label>
                            <Field as="select" name="emotionalFactorsQ3" className="fieldInput">
                              <option value="">Selecione...</option>
                              <option value="nunca">Nunca</option>
                              <option value="raramente">Raramente</option>
                              <option value="as_vezes">Às vezes</option>
                              <option value="frequentemente">Frequentemente</option>
                              <option value="sempre">Sempre</option>
                            </Field>
                            {errors.emotionalFactorsQ3 && touched.emotionalFactorsQ3 && <div className="error">{errors.emotionalFactorsQ3}</div>}
                          </div>

                          <div className="consultationLineQuestion">
                            <label htmlFor="emotionalFactorsQ4">Tem algum hábito alimentar que gostaria de mudar?</label>
                            <Field
                              type="text"
                              name="emotionalFactorsQ4"
                              id="emotionalFactorsQ4"
                              className="fieldInput"
                            />
                            {errors.emotionalFactorsQ4 && touched.emotionalFactorsQ4 && <div className="error">{errors.emotionalFactorsQ4}</div>}
                          </div>
                        </div>
                        {/* Suplementação e Consumo de Vitaminas */}
                        <div className="consultation-form-group">
                          <h3 className='askTitle'><span className='askSpan'>7</span> Suplementação e Consumo de Vitaminas</h3>

                          <div className="consultationLineQuestion">
                            <label htmlFor="supplementsQ1">Faz uso de suplementos? (Proteína, ômega-3, vitaminas, etc.)</label>
                            <Field
                              as="select"
                              name="supplementsQ1"
                              id="supplementsQ1"
                              className="fieldInput"
                            >
                              <option value="">Selecione...</option>
                              <option value="sim">Sim</option>
                              <option value="nao">Não</option>
                              <option value="ocasionalmente">Ocasionalmente</option>
                            </Field>
                            {errors.supplementsQ1 && touched.supplementsQ1 && <div className="error">{errors.supplementsQ1}</div>}
                          </div>

                          <div className="consultationLineQuestion">
                            <label htmlFor="supplementsQ2">Tem alguma orientação médica ou nutricional para o uso?</label>
                            <Field
                              as="select"
                              name="supplementsQ2"
                              id="supplementsQ2"
                              className="fieldInput"
                            >
                              <option value="">Selecione...</option>
                              <option value="sim_medica">Sim, médica</option>
                              <option value="sim_nutricional">Sim, nutricional</option>
                              <option value="nao">Não</option>
                              <option value="auto">Autoprescrição</option>
                            </Field>
                            {errors.supplementsQ2 && touched.supplementsQ2 && <div className="error">{errors.supplementsQ2}</div>}
                          </div>
                        </div>
                        {/* Plano e Expectativa */}
                        <div className="consultation-form-group">
                          <h3 className='askTitle'><span className='askSpan'>8</span> Plano e Expectativas</h3>

                          <div className="consultationLineQuestion">
                            <label htmlFor="expectationsQ1">O que espera da sua reeducação alimentar?</label>
                            <Field
                              as="textarea"
                              name="expectationsQ1"
                              id="expectationsQ1"
                              className="fieldInput textarea_field"
                            />
                            {errors.expectationsQ1 && touched.expectationsQ1 && <div className="error">{errors.expectationsQ1}</div>}
                          </div>

                          <div className="consultationLineQuestion">
                            <label htmlFor="expectationsQ3">Tem alguma preferência ou restrição para o planejamento alimentar?</label>
                            <Field
                              as="textarea"
                              name="expectationsQ3"
                              id="expectationsQ3"
                              className="fieldInput textarea_field"
                              placeholder="Ex.: Vegetarianismo, alergias, aversões alimentares..."
                            />
                            {errors.expectationsQ3 && touched.expectationsQ3 && <div className="error">{errors.expectationsQ3}</div>}
                          </div>

                          <div className="consultationLineQuestion">
                            <label htmlFor="expectationsQ2">Está disposto(a) a mudar hábitos alimentares e de estilo de vida?</label>
                            <Field
                              as="select"
                              name="expectationsQ2"
                              id="expectationsQ2"
                              className="fieldInput"
                            >
                              <option value="">Selecione...</option>
                              <option value="totalmente">Totalmente disposto(a)</option>
                              <option value="parcialmente">Parcialmente disposto(a)</option>
                              <option value="dificuldade">Com alguma dificuldade</option>
                              <option value="indeciso">Indeciso(a)/Não tenho certeza</option>
                            </Field>
                            {errors.expectationsQ2 && touched.expectationsQ2 && <div className="error">{errors.expectationsQ2}</div>}
                          </div>
                        </div>
                        {/* Considerações Finais */}
                        <div className="consultation-form-group">
                          <div className="consultationLineQuestion">
                            <h3 className='askTitle'><span className='askSpan'>9</span> Considerações Finais</h3>
                            <label>Procedimentos:</label>
                            <Field as="textarea" name="procedures" />
                            {errors.procedures && touched.procedures && <div className="error">{errors.procedures}</div>}
                          </div>
                          <div className="consultationLineQuestion">
                            <label>Diagnóstico:</label>
                            <Field as="textarea" name="diagnosis" />
                            {errors.diagnosis && touched.diagnosis && <div className="error">{errors.diagnosis}</div>}
                          </div>
                          <div className="consultationLineQuestion">
                            <label>Observações:</label>
                            <Field as="textarea" name="observations" />
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedLayout === 'horizontal' && (
                      <div id="horizontal-layout">
                        {/* SEU LAYOUT HORIZONTAL AQUI */}
                        <Tabs errors={errors} tabFieldMapping={tabFieldMapping}>
                          <Tab label="Nessa Consulta">
                            <div className="consultationLine">
                              <div className="consultation-form-group">
                                <label>Data da Consulta:</label>
                                <Field type="date" id="consultation_date" name="consult_date" />
                                {errors.consult_date && touched.consult_date && <div className="error">{errors.consult_date}</div>}
                              </div>

                              <div className="consultation-form-group">
                                <label>Hora de Início:</label>
                                <Field type="time" id="consultation_time" name="start_time" />
                                {errors.start_time && touched.start_time && <div className="error">{errors.start_time}</div>}
                              </div>

                              <div className="consultation-form-group">
                                <label htmlFor="consultation_type_id">Tipo de Consulta:</label>
                                {typesLoading ? (
                                  <div>Carregando tipos de consulta...</div>
                                ) : typesError ? (
                                  <div className="error">Erro ao carregar tipos: {typesError}</div>
                                ) : (
                                  <Select
                                    name="consultation_type_id"
                                    options={consultationTypes.map(type => ({
                                      value: type.consultation_type_id,
                                      label: `${type.name} (R$ ${type.default_fee.toFixed(2)})`,
                                      fee: type.default_fee // Podemos armazenar a taxa para uso posterior
                                    }))}
                                    value={
                                      consultationTypes
                                        .map(type => ({
                                          value: type.consultation_type_id,
                                          label: `${type.name} (R$ ${type.default_fee.toFixed(2)})`,
                                          fee: type.default_fee
                                        }))
                                        .find(option => option.value === values.consultation_type_id) || null
                                    }
                                    onChange={(selectedOption) => {
                                      setFieldValue('consultation_type_id', selectedOption.value);
                                      setFieldValue('base_fee', selectedOption.fee);
                                      setFieldValue('final_fee', calculateFees(selectedOption.fee, values.additional_charges, values.discounts));
                                    }}

                                    placeholder="Selecione o tipo de consulta"
                                    noOptionsMessage={() => "Nenhum tipo disponível"}
                                    classNamePrefix="custom-select"
                                  />
                                )}
                                {errors.start_time && touched.consultation_type_id && <div className="error">{errors.consultation_type_id}</div>}
                                <ErrorMessage name="consultation_type_id" component="div" className="error" />
                              </div>

                            </div>
                            <div className="consultationLine">
                              <div className="consultationLine">
                                {/* Campo Taxa Base */}
                                <div className="consultation-form-group">
                                  <label>Taxa Base (R$)</label>
                                  <Field
                                    name="base_fee"
                                    onBlur={(e) => {
                                      const value = parseFloat(e.target.value) || 0;
                                      setFieldValue('base_fee', value.toFixed(2));
                                      setFieldValue('final_fee', calculateFees(
                                        value,
                                        values.additional_charges,
                                        values.discounts
                                      ));
                                    }}
                                    onChange={(e) => {
                                      // Permite digitação livre, só formata no blur
                                      setFieldValue('base_fee', e.target.value);
                                    }}
                                    value={values.base_fee}
                                    type="number"
                                    step="5.00"
                                    className="form-control"
                                  />
                                </div>

                                {/* Campo Encargos Adicionais */}
                                <div className="consultation-form-group">
                                  <label>Encargos Adicionais (R$)</label>
                                  <Field
                                    name="additional_charges"
                                    onBlur={(e) => {
                                      const value = parseFloat(e.target.value) || 0;
                                      setFieldValue('additional_charges', value.toFixed(2));
                                      setFieldValue('final_fee', calculateFees(
                                        values.base_fee,
                                        value,
                                        values.discounts
                                      ));
                                    }}
                                    onChange={(e) => {
                                      setFieldValue('additional_charges', e.target.value);
                                    }}
                                    value={values.additional_charges}
                                    type="number"
                                    step="5.00"
                                    className="form-control"
                                  />
                                </div>

                                {/* Campo Descontos */}
                                <div className="consultation-form-group">
                                  <label>Descontos (R$)</label>
                                  <Field
                                    name="discounts"
                                    onBlur={(e) => {
                                      const value = parseFloat(e.target.value) || 0;
                                      setFieldValue('discounts', value.toFixed(2));
                                      setFieldValue('final_fee', calculateFees(
                                        values.base_fee,
                                        values.additional_charges,
                                        value
                                      ));
                                    }}
                                    onChange={(e) => {
                                      setFieldValue('discounts', e.target.value);
                                    }}
                                    value={values.discounts}
                                    type="number"
                                    step="5.00"
                                    className="form-control"
                                  />
                                </div>

                                {/* Campo Valor Final (somente leitura) */}
                                <div className="consultation-form-group">
                                  <label>Valor Final (R$)</label>
                                  <Field
                                    name="final_fee"
                                    type="text"
                                    value={parseFloat(values.final_fee || 0).toFixed(2)}
                                    readOnly
                                    className="form-control"
                                  />
                                </div>
                              </div>
                            </div>
                          </Tab>
                          {/* Objetivo da Consulta */}
                          <Tab label='1 Objetivo da Consulta'>
                            <div className="consultation-form-group">
                              <h3 className='askTitle'><span className='askSpan'>1</span> Objetivo da Consulta</h3>
                              <div className="consultationLineQuestion">
                                <label htmlFor="consult_weight">Peso Atual (kg):</label>
                                <Field
                                  type="number"
                                  name="consult_weight"
                                  id="consult_weight"
                                  className="fieldInput"
                                  onKeyDown={(e) => onlyNumbers(e, setFieldValue, 'consult_weight')}
                                />
                                {errors.consult_weight && touched.consult_weight && <div className="error">{errors.consult_weight}</div>}
                              </div>
                            </div>
                            <div className="consultation-form-group">
                              <div className="consultationLineQuestion">
                                <label htmlFor="objectiveQ1">Qual o motivo principal que trouxe você aqui?</label>
                                <Field
                                  type="text"
                                  name="objectiveQ1"
                                  id="objectiveQ1"
                                  className="fieldInput"
                                />
                                {errors.objectiveQ1 && touched.objectiveQ1 && <div className="error">{errors.objectiveQ1}</div>}
                              </div>
                              <div className="consultationLineQuestion">
                                <label htmlFor="objectiveQ2">Tem alguma meta específica? (Ex.: emagrecimento, ganho de massa, melhora da saúde, controle de doenças, etc.)</label>
                                <Field
                                  type="text"
                                  name="objectiveQ2"
                                  id="objectiveQ2"
                                  className="fieldInput"
                                />
                                {errors.objectiveQ2 && touched.objectiveQ2 && <div className="error">{errors.objectiveQ2}</div>}
                              </div>
                              <div className="consultationLineQuestion">
                                <label htmlFor="objectiveQ3">Tem alguma dificuldade específica com sua alimentação?</label>
                                <Field
                                  type="text"
                                  name="objectiveQ3"
                                  id="objectiveQ3"
                                  className="fieldInput"
                                />
                                {errors.objectiveQ3 && touched.objectiveQ3 && <div className="error">{errors.objectiveQ3}</div>}
                              </div>
                            </div>
                          </Tab>
                          {/* Histórico Clínico Familiar */}
                          <Tab label="2 Histórico Clínico Familiar">
                            <h3 className='askTitle'><span className='askSpan'>2</span> Histórico Clínico Familiar</h3>
                            <div className="consultation-form-group">
                              {/* Arrays de Campos (Ex.: Doenças Atuais) */}
                              <div className="consultation-form-group">
                                <label>Doenças Atuais:</label>
                                <FieldArray name="current_illnesses">
                                  {({ push, remove }) => (
                                    <div>
                                      {values.current_illnesses && values.current_illnesses.length > 0 ? (
                                        values.current_illnesses.map((illness, index) => (
                                          <div key={index} className="consultationLine">
                                            <Field
                                              type="text"
                                              name={`current_illnesses[${index}]`}
                                              placeholder={`Doença ${index + 1}`}
                                            />
                                            <button className='btnIcoAddRm' type="button" onClick={() => push('')} data-tip={addBtn}>
                                              <FaPlusCircle className='iconPlus' />
                                            </button>
                                            <button className='btnIcoAddRm' type="button" onClick={() => remove(index)} data-tip={remBtn}>
                                              <FaMinusCircle className='iconMinus' />
                                            </button>
                                          </div>
                                        ))
                                      ) : (
                                        <>
                                          <p>Nenhuma doença atual adicionada.</p>
                                          <button className='btnIcoAddRm' type="button" onClick={() => push('')} data-tip={addBtn}>
                                            <FaPlusCircle className='iconPlus' />
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </FieldArray>
                                {errors.current_illnesses && touched.current_illnesses && (
                                  <div className="error-message">{errors.current_illnesses}</div>
                                )}
                              </div>

                              {/* Doenças Prévias ou Crônicas */}
                              <div className="consultation-form-group">
                                <label>Doenças Prévias ou Crônicas</label>
                                <FieldArray name="past_illnesses" id="past_illnesses">
                                  {({ push, remove }) => (
                                    <div>
                                      {values.past_illnesses && values.past_illnesses.length > 0 ? (
                                        values.past_illnesses.map((illness, index) => (
                                          <div key={index} className="consultationLine">
                                            <Field
                                              type="text"
                                              name={`past_illnesses[${index}]`}
                                              placeholder={`Doença ${index + 1}`}
                                            />
                                            <button className='btnIcoAddRm' type="button" onClick={() => push('')} data-tip={addBtn}>
                                              <FaPlusCircle className='iconPlus' />
                                            </button>
                                            <button className='btnIcoAddRm' type="button" onClick={() => remove(index)} data-tip={remBtn}>
                                              <FaMinusCircle className='iconMinus' />
                                            </button>
                                          </div>
                                        ))
                                      ) : (
                                        <>
                                          <p>Nenhuma Doença Prévias ou Crônicas.</p>
                                          <button className='btnIcoAddRm' type="button" onClick={() => push('')} data-tip={addBtn}>
                                            <FaPlusCircle className='iconPlus' />
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </FieldArray>
                                {errors.past_illnesses && touched.past_illnesses && (
                                  <div className="error-message">{errors.past_illnesses}</div>
                                )}
                              </div>

                              {/* Alergias e Intolerâncias */}
                              <div className="consultation-form-group">
                                <label>Alergias e Intolerâncias</label>
                                <FieldArray name="allergies" id="allergies">
                                  {({ push, remove }) => (
                                    <div>
                                      {values.allergies && values.allergies.length > 0 ? (
                                        values.allergies.map((allergy, index) => (
                                          <div key={index} className="consultationLine">
                                            <Field
                                              type="text"
                                              name={`allergies[${index}]`}
                                              placeholder={`Alergia ${index + 1}`}
                                            />
                                            <button className="btnIcoAddRm" type="button" onClick={() => push('')} data-tip={addBtn}>
                                              <FaPlusCircle className="iconPlus" />
                                            </button>
                                            <button className="btnIcoAddRm" type="button" onClick={() => remove(index)} data-tip={remBtn}>
                                              <FaMinusCircle className="iconMinus" />
                                            </button>
                                          </div>
                                        ))
                                      ) : (
                                        <>
                                          <p>Nenhuma alergia adicionada.</p>
                                          <button className="btnIcoAddRm" type="button" onClick={() => push('')} data-tip={addBtn}>
                                            <FaPlusCircle className="iconPlus" />
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </FieldArray>
                                {errors.allergies && touched.allergies && (
                                  <div className="error-message">{errors.allergies}</div>
                                )}
                              </div>

                              {/* Cirurgias e Hospitalizações */}
                              <div className="consultation-form-group">
                                <label>Cirurgias e Hospitalizações</label>
                                <FieldArray name="surgeries" id="surgeries">
                                  {({ push, remove }) => (
                                    <div>
                                      {values.surgeries && values.surgeries.length > 0 ? (
                                        values.surgeries.map((surgery, index) => (
                                          <div key={index} className="consultationLine">
                                            <Field
                                              type="text"
                                              name={`surgeries[${index}]`}
                                              placeholder={`Cirurgia ${index + 1}`}
                                            />
                                            <button className="btnIcoAddRm" type="button" onClick={() => push('')} data-tip={addBtn}>
                                              <FaPlusCircle className="iconPlus" />
                                            </button>
                                            <button className="btnIcoAddRm" type="button" onClick={() => remove(index)} data-tip={remBtn}>
                                              <FaMinusCircle className="iconMinus" />
                                            </button>
                                          </div>
                                        ))
                                      ) : (
                                        <>
                                          <p>Nenhuma cirurgia ou hospitalização adicionada.</p>
                                          <button className="btnIcoAddRm" type="button" onClick={() => push('')} data-tip={addBtn}>
                                            <FaPlusCircle className="iconPlus" />
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </FieldArray>
                                {errors.surgeries && touched.surgeries && (
                                  <div className="error-message">{errors.surgeries}</div>
                                )}
                              </div>

                              {/* Uso de Medicamentos */}
                              <div className="consultation-form-group">
                                <label>Uso de Medicamentos</label>
                                <FieldArray name="medications" id="medications">
                                  {({ push, remove }) => (
                                    <div>
                                      {values.medications && values.medications.length > 0 ? (
                                        values.medications.map((medication, index) => (
                                          <div key={index} className="consultationLine">
                                            <Field
                                              type="text"
                                              name={`medications[${index}]`}
                                              placeholder={`Medicamento ${index + 1}`}
                                            />
                                            <button className="btnIcoAddRm" type="button" onClick={() => push('')} data-tip={addBtn}>
                                              <FaPlusCircle className="iconPlus" />
                                            </button>
                                            <button className="btnIcoAddRm" type="button" onClick={() => remove(index)} data-tip={remBtn}>
                                              <FaMinusCircle className="iconMinus" />
                                            </button>
                                          </div>
                                        ))
                                      ) : (
                                        <>
                                          <p>Nenhum medicamento adicionado.</p>
                                          <button className="btnIcoAddRm" type="button" onClick={() => push('')} data-tip={addBtn}>
                                            <FaPlusCircle className="iconPlus" />
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </FieldArray>
                                {errors.medications && touched.medications && (
                                  <div className="error-message">{errors.medications}</div>
                                )}
                              </div>

                              {/* Histórico Familiar */}
                              <div className="consultation-form-group">
                                <label>Histórico Familiar</label>
                                <FieldArray name="family_history" id="family_history">
                                  {({ push, remove }) => (
                                    <div>
                                      {values.family_history && values.family_history.length > 0 ? (
                                        values.family_history.map((history, index) => (
                                          <div key={index} className="consultationLine">
                                            <Field
                                              type="text"
                                              name={`family_history[${index}]`}
                                              placeholder={`Histórico ${index + 1}`}
                                            />
                                            <button className="btnIcoAddRm" type="button" onClick={() => push('')} data-tip={addBtn}>
                                              <FaPlusCircle className="iconPlus" />
                                            </button>
                                            <button className="btnIcoAddRm" type="button" onClick={() => remove(index)} data-tip={remBtn}>
                                              <FaMinusCircle className="iconMinus" />
                                            </button>
                                          </div>
                                        ))
                                      ) : (
                                        <>
                                          <p>Nenhum histórico familiar adicionado.</p>
                                          <button className="btnIcoAddRm" type="button" onClick={() => push('')} data-tip={addBtn}>
                                            <FaPlusCircle className="iconPlus" />
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </FieldArray>
                                {errors.family_history && touched.family_history && (
                                  <div className="error-message">{errors.family_history}</div>
                                )}
                              </div>
                            </div>

                          </Tab>
                          {/* Histórico Alimentar */}
                          <Tab label="3 - Histórico Alimentar">
                            <div className="consultation-form-group">
                              <h3 className='askTitle'><span className='askSpan'>3</span> Histórico Alimentar</h3>
                              <div className="consultationLineQuestion">
                                <label htmlFor="foodHistoryQ1">Quantas refeições faz ao dia?</label>
                                <Field
                                  type="text"
                                  name="foodHistoryQ1"
                                  id="foodHistoryQ1"
                                  className="fieldInput"
                                />
                                {errors.foodHistoryQ1 && touched.foodHistoryQ1 && <div className="error">{errors.foodHistoryQ1}</div>}
                              </div>

                              <div className="consultationLineQuestion">
                                <label htmlFor="foodHistoryQ2">Costuma pular alguma refeição?</label>
                                <Field
                                  type="text"
                                  name="foodHistoryQ2"
                                  id="foodHistoryQ2"
                                  className="fieldInput"
                                />
                                {errors.foodHistoryQ2 && touched.foodHistoryQ2 && <div className="error">{errors.foodHistoryQ2}</div>}
                              </div>

                              <div className="consultationLineQuestion">
                                <label htmlFor="foodHistoryQ3">Como é o seu café da manhã, almoço e jantar? (Ex.: horário, composição dos pratos, quantidade)</label>
                                <Field
                                  type="text"
                                  name="foodHistoryQ3"
                                  id="foodHistoryQ3"
                                  className="fieldInput"
                                />
                                {errors.foodHistoryQ3 && touched.foodHistoryQ3 && <div className="error">{errors.foodHistoryQ3}</div>}
                              </div>

                              <div className="consultationLineQuestion">
                                <label htmlFor="foodHistoryQ4">Consome lanches entre as refeições? O que costuma comer?</label>
                                <Field
                                  type="text"
                                  name="foodHistoryQ4"
                                  id="foodHistoryQ4"
                                  className="fieldInput"
                                />
                                {errors.foodHistoryQ4 && touched.foodHistoryQ4 && <div className="error">{errors.foodHistoryQ4}</div>}
                              </div>

                              <div className="consultationLineQuestion">
                                <label htmlFor="foodHistoryQ5">Prefere alimentos doces ou salgados?</label>
                                <Field
                                  type="text"
                                  name="foodHistoryQ5"
                                  id="foodHistoryQ5"
                                  className="fieldInput"
                                />
                                {errors.foodHistoryQ5 && touched.foodHistoryQ5 && <div className="error">{errors.foodHistoryQ5}</div>}
                              </div>

                              <div className="consultationLineQuestion">
                                <label htmlFor="foodHistoryQ6">Qual a frequência de consumo de fast food e ultraprocessados?</label>
                                <Field
                                  type="text"
                                  name="foodHistoryQ6"
                                  id="foodHistoryQ6"
                                  className="fieldInput"
                                />
                                {errors.foodHistoryQ6 && touched.foodHistoryQ6 && <div className="error">{errors.foodHistoryQ6}</div>}
                              </div>

                              <div className="consultationLineQuestion">
                                <label htmlFor="foodHistoryQ7">Tem alguma restrição alimentar (ética, religiosa, voluntária)?</label>
                                <Field
                                  type="text"
                                  name="foodHistoryQ7"
                                  id="foodHistoryQ7"
                                  className="fieldInput"
                                />
                                {errors.foodHistoryQ7 && touched.foodHistoryQ7 && <div className="error">{errors.foodHistoryQ7}</div>}
                              </div>

                              <div className="consultationLineQuestion">
                                <label htmlFor="foodHistoryQ8">Sabe cozinhar? Com que frequência prepara suas refeições?</label>
                                <Field
                                  type="text"
                                  name="foodHistoryQ8"
                                  id="foodHistoryQ8"
                                  className="fieldInput"
                                />
                                {errors.foodHistoryQ8 && touched.foodHistoryQ8 && <div className="error">{errors.foodHistoryQ8}</div>}
                              </div>

                              <div className="consultationLineQuestion">
                                <label htmlFor="foodHistoryQ9">Costuma comer fora ou pedir delivery?</label>
                                <Field
                                  type="text"
                                  name="foodHistoryQ9"
                                  id="foodHistoryQ9"
                                  className="fieldInput"
                                />
                                {errors.foodHistoryQ9 && touched.foodHistoryQ9 && <div className="error">{errors.foodHistoryQ9}</div>}
                              </div>
                            </div>
                          </Tab>
                          {/*Hidratação  */}
                          <Tab label="4 - Hidratação">
                            <div className="consultation-form-group">
                              <h3 className='askTitle'><span className='askSpan'>4</span> Hidratação</h3>

                              <div className="consultationLineQuestion">
                                <label htmlFor="hydrationQ1">Quantos litros de água consome por dia?</label>
                                <Field
                                  type="text"
                                  name="hydrationQ1"
                                  id="hydrationQ1"
                                  className="fieldInput"
                                />
                                {errors.hydrationQ1 && touched.hydrationQ1 && <div className="error">{errors.hydrationQ1}</div>}
                              </div>

                              <div className="consultationLineQuestion">
                                <label htmlFor="hydrationQ2">Bebe outras bebidas com frequência? (Refrigerantes, sucos industrializados, álcool, etc.)</label>
                                <Field
                                  type="text"
                                  name="hydrationQ2"
                                  id="hydrationQ2"
                                  className="fieldInput"
                                />
                                {errors.hydrationQ2 && touched.hydrationQ2 && <div className="error">{errors.hydrationQ2}</div>}
                              </div>

                              <div className="consultationLineQuestion">
                                <label htmlFor="hydrationQ3">Sente sede constantemente?</label>
                                <Field
                                  type="text"
                                  name="hydrationQ3"
                                  id="hydrationQ3"
                                  className="fieldInput"
                                />
                                {errors.hydrationQ3 && touched.hydrationQ3 && <div className="error">{errors.hydrationQ3}</div>}
                              </div>
                            </div>
                          </Tab>
                          {/* `Hábitos de Vida e Rotina */}
                          <Tab label="5 - Hábitos de Vida e Rotina">
                            <div className="consultation-form-group">
                              <h3 className='askTitle'><span className='askSpan'>5</span> Hábitos de Vida e Rotina</h3>

                              <div className="consultationLineQuestion">
                                <label htmlFor="habitsQ1">Pratica atividade física? (Qual, frequência e intensidade?)</label>
                                <Field
                                  type="text"
                                  name="habitsQ1"
                                  id="habitsQ1"
                                  className="fieldInput"
                                />
                                {errors.habitsQ1 && touched.habitsQ1 && <div className="error">{errors.habitsQ1}</div>}
                              </div>

                              <div className="consultationLineQuestion">
                                <label htmlFor="habitsQ2">Como é seu nível de estresse diário?</label>
                                <Field
                                  type="text"
                                  name="habitsQ2"
                                  id="habitsQ2"
                                  className="fieldInput"
                                />
                                {errors.habitsQ2 && touched.habitsQ2 && <div className="error">{errors.habitsQ2}</div>}
                              </div>

                              <div className="consultationLineQuestion">
                                <label htmlFor="habitsQ3">Como avalia seu sono? (Horas dormidas, qualidade do sono)</label>
                                <Field
                                  type="text"
                                  name="habitsQ3"
                                  id="habitsQ3"
                                  className="fieldInput"
                                />
                                {errors.habitsQ3 && touched.habitsQ3 && <div className="error">{errors.habitsQ3}</div>}
                              </div>

                              <div className="consultationLineQuestion">
                                <label htmlFor="habitsQ4">Tem horários regulares para refeições e descanso?</label>
                                <Field
                                  type="text"
                                  name="habitsQ4"
                                  id="habitsQ4"
                                  className="fieldInput"
                                />
                                {errors.habitsQ4 && touched.habitsQ4 && <div className="error">{errors.habitsQ4}</div>}
                              </div>

                              <div className="consultationLineQuestion">
                                <label htmlFor="habitsQ5">Faz uso de álcool ou tabaco?</label>
                                <Field
                                  type="text"
                                  name="habitsQ5"
                                  id="habitsQ5"
                                  className="fieldInput"
                                />
                                {errors.habitsQ5 && touched.habitsQ5 && <div className="error">{errors.habitsQ5}</div>}
                              </div>
                            </div>
                          </Tab>
                          {/* Fatores Emocionais e Comportamentais */}
                          <Tab label="6 - Fatores Emocionais / Comportamentais">
                            <div className="consultation-form-group">
                              <h3 className='askTitle'><span className='askSpan'>6</span> Fatores Emocionais e Comportamentais</h3>

                              <div className="consultationLineQuestion">
                                <label htmlFor="emotionalFactorsQ1">Sente ansiedade ou compulsão alimentar?</label>
                                <Field as="select" name="emotionalFactorsQ1" className="fieldInput">
                                  <option value="">Selecione...</option>
                                  <option value="nunca">Nunca</option>
                                  <option value="raramente">Raramente</option>
                                  <option value="as_vezes">Às vezes</option>
                                  <option value="frequentemente">Frequentemente</option>
                                  <option value="sempre">Sempre</option>
                                </Field>
                                {errors.emotionalFactorsQ1 && touched.emotionalFactorsQ1 && <div className="error">{errors.emotionalFactorsQ1}</div>}
                              </div>

                              <div className="consultationLineQuestion">
                                <label htmlFor="emotionalFactorsQ2">Costuma comer por impulso ou emocionalmente?</label>
                                <Field as="select" name="emotionalFactorsQ2" className="fieldInput">
                                  <option value="">Selecione...</option>
                                  <option value="nunca">Nunca</option>
                                  <option value="raramente">Raramente</option>
                                  <option value="as_vezes">Às vezes</option>
                                  <option value="frequentemente">Frequentemente</option>
                                  <option value="sempre">Sempre</option>
                                </Field>
                                {errors.emotionalFactorsQ2 && touched.emotionalFactorsQ2 && <div className="error">{errors.emotionalFactorsQ2}</div>}
                              </div>

                              <div className="consultationLineQuestion">
                                <label htmlFor="emotionalFactorsQ3">Tem momentos de exagero alimentar? (Ex.: comer muito à noite, beliscar constantemente)</label>
                                <Field as="select" name="emotionalFactorsQ3" className="fieldInput">
                                  <option value="">Selecione...</option>
                                  <option value="nunca">Nunca</option>
                                  <option value="raramente">Raramente</option>
                                  <option value="as_vezes">Às vezes</option>
                                  <option value="frequentemente">Frequentemente</option>
                                  <option value="sempre">Sempre</option>
                                </Field>
                                {errors.emotionalFactorsQ3 && touched.emotionalFactorsQ3 && <div className="error">{errors.emotionalFactorsQ3}</div>}
                              </div>

                              <div className="consultationLineQuestion">
                                <label htmlFor="emotionalFactorsQ4">Tem algum hábito alimentar que gostaria de mudar?</label>
                                <Field
                                  type="text"
                                  name="emotionalFactorsQ4"
                                  id="emotionalFactorsQ4"
                                  className="fieldInput"
                                />
                                {errors.emotionalFactorsQ4 && touched.emotionalFactorsQ4 && <div className="error">{errors.emotionalFactorsQ4}</div>}
                              </div>
                            </div>
                          </Tab>
                          {/* Suplementação e Consumo de Vitaminas */}
                          <Tab label="7 - Suplementação e Vitaminas">
                            <div className="consultation-form-group">
                              <h3 className='askTitle'><span className='askSpan'>7</span> Suplementação e Consumo de Vitaminas</h3>

                              <div className="consultationLineQuestion">
                                <label htmlFor="supplementsQ1">Faz uso de suplementos? (Proteína, ômega-3, vitaminas, etc.)</label>
                                <Field
                                  as="select"
                                  name="supplementsQ1"
                                  id="supplementsQ1"
                                  className="fieldInput"
                                >
                                  <option value="">Selecione...</option>
                                  <option value="sim">Sim</option>
                                  <option value="nao">Não</option>
                                  <option value="ocasionalmente">Ocasionalmente</option>
                                </Field>
                                {errors.supplementsQ1 && touched.supplementsQ1 && <div className="error">{errors.supplementsQ1}</div>}
                              </div>

                              <div className="consultationLineQuestion">
                                <label htmlFor="supplementsQ2">Tem alguma orientação médica ou nutricional para o uso?</label>
                                <Field
                                  as="select"
                                  name="supplementsQ2"
                                  id="supplementsQ2"
                                  className="fieldInput"
                                >
                                  <option value="">Selecione...</option>
                                  <option value="sim_medica">Sim, médica</option>
                                  <option value="sim_nutricional">Sim, nutricional</option>
                                  <option value="nao">Não</option>
                                  <option value="auto">Autoprescrição</option>
                                </Field>
                                {errors.supplementsQ2 && touched.supplementsQ2 && <div className="error">{errors.supplementsQ2}</div>}
                              </div>
                            </div>
                          </Tab>
                          {/* Plano e Expectativa */}
                          <Tab label="8 - Planos e Expectativas">
                            <div className="consultation-form-group">
                              <h3 className='askTitle'><span className='askSpan'>8</span> Plano e Expectativas</h3>

                              <div className="consultationLineQuestion">
                                <label htmlFor="expectationsQ1">O que espera da sua reeducação alimentar?</label>
                                <Field
                                  type="text"
                                  name="expectationsQ1"
                                  id="expectationsQ1"
                                  className="fieldInput"
                                />
                                {errors.expectationsQ1 && touched.expectationsQ1 && <div className="error">{errors.expectationsQ1}</div>}
                              </div>

                              <div className="consultationLineQuestion">
                                <label htmlFor="expectationsQ3">Tem alguma preferência ou restrição para o planejamento alimentar?</label>
                                <Field
                                  type="text"
                                  name="expectationsQ3"
                                  id="expectationsQ3"
                                  className="fieldInput"
                                  placeholder="Ex.: Vegetarianismo, alergias, aversões alimentares..."
                                />
                                {errors.expectationsQ3 && touched.expectationsQ3 && <div className="error">{errors.expectationsQ3}</div>}
                              </div>

                              <div className="consultationLineQuestion">
                                <label htmlFor="expectationsQ2">Está disposto(a) a mudar hábitos alimentares e de estilo de vida?</label>
                                <Field
                                  as="select"
                                  name="expectationsQ2"
                                  id="expectationsQ2"
                                  className="fieldInput"
                                >
                                  <option value="">Selecione...</option>
                                  <option value="totalmente">Totalmente disposto(a)</option>
                                  <option value="parcialmente">Parcialmente disposto(a)</option>
                                  <option value="dificuldade">Com alguma dificuldade</option>
                                  <option value="indeciso">Indeciso(a)/Não tenho certeza</option>
                                </Field>
                                {errors.expectationsQ2 && touched.expectationsQ2 && <div className="error">{errors.expectationsQ2}</div>}
                              </div>
                            </div>
                          </Tab>
                          {/* Considerações Finais */}
                          <Tab label="9 - Considerações Finais">
                            <div className="consultation-form-group">
                              <div className="consultationLineQuestion">
                                <h3 className='askTitle'><span className='askSpan'>9</span> Considerações Finais</h3>
                                <label>Procedimentos:</label>
                                <Field as="textarea" name="procedures" />
                                {errors.procedures && touched.procedures && <div className="error">{errors.procedures}</div>}
                              </div>
                              <div className="consultationLineQuestion">
                                <label>Diagnóstico:</label>
                                <Field as="textarea" name="diagnosis" />
                                {errors.diagnosis && touched.diagnosis && <div className="error">{errors.diagnosis}</div>}
                              </div>
                              <div className="consultationLineQuestion">
                                <label>Observações:</label>
                                <Field as="textarea" name="observations" />
                              </div>
                            </div>
                          </Tab>
                        </Tabs>
                      </div>
                    )}
                  </div>
                </div>

              </div>
              {/* Alertas de Rodapé */}
              <div className="consultationButtons">
                {errors.consult_date && touched.consult_date && <div className="error">{errors.consult_date}</div>}
                {errors.start_time && touched.start_time && <div className="error">{errors.start_time}</div>}
                {errors.start_time && touched.consultation_type_id && <div className="error">{errors.consultation_type_id}</div>}
                {errors.current_weight && touched.current_weight && <div className="error">{errors.current_weight}</div>}
                {errors.consult_weight && touched.consult_weight && <div className="error">{errors.consult_weight}</div>}
                {errors.objectiveQ1 && touched.objectiveQ1 && <div className="error">{errors.objectiveQ1}</div>}
                {errors.objectiveQ2 && touched.objectiveQ2 && <div className="error">{errors.objectiveQ2}</div>}
                {errors.objectiveQ3 && touched.objectiveQ3 && <div className="error">{errors.objectiveQ3}</div>}

                {errors.foodHistoryQ1 && touched.foodHistoryQ1 && <div className="error">{errors.foodHistoryQ1}</div>}

                {errors.family_history && touched.family_history && (<div className="error-message">{errors.family_history}</div>)}
                {errors.allergies && touched.allergies && (<div className="error-message">{errors.allergies}</div>)}

                {errors.expectationsQ1 && touched.expectationsQ1 && <div className="error">{errors.expectationsQ1}</div>}
                {errors.expectationsQ2 && touched.expectationsQ2 && <div className="error">{errors.expectationsQ2}</div>}
                {errors.expectationsQ3 && touched.expectationsQ3 && <div className="error">{errors.expectationsQ3}</div>}
                {errors.medications && touched.medications && <div className="error">{errors.medications}</div>}

                {errors.emotionalFactorsQ1 && touched.emotionalFactorsQ1 && <div className="error">{errors.emotionalFactorsQ1}</div>}


                {errors.family_history_relation && touched.family_history_relation && <div className="error">{errors.family_history_relation}</div>}
                {errors.family_history_description && touched.family_history_description && <div className="error">{errors.family_history_description}</div>}



                {errors.procedures && touched.procedures && <div className="error">{errors.procedures}</div>}
                {errors.diagnosis && touched.diagnosis && <div className="error">{errors.diagnosis}</div>}
                {errors.observations && touched.observations && <div className="error">{errors.observations}</div>}



                <ErrorMessage name="consultation_type_id" component="div" className="error" />
                <ErrorMessage name="patient_id" component="div" className="error" />
                <ErrorMessage name="appointment_id" component="div" className="error" />
                <ErrorMessage name="company_id" component="div" className="error" />
                <ErrorMessage name="professional_id" component="div" className="error" />
                <ErrorMessage name="diet_id" component="div" className="error" />
              </div>

              {/* Buttons */}
              <div className="consultationButtons">
                <button className="sendBtn" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Salvando...' : 'Salvar Consulta'}
                </button>
                <button className="backBtn" type="button" onClick={() => navigate('/home/appointment/get')}>
                  Cancelar
                </button>
              </div>
            </Form>
          )
          }
        </Formik >

      </div >
    </div >
  );
};

export default CreateConsultation;
